import React, { useEffect, useMemo, useState } from "react";
import { useRole } from "../context/RoleContext";
import { MapContainer, TileLayer, Polygon, Tooltip, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { getHeatmap, exportUrl } from "../lib/cityPortal";
import { Download, AlertTriangle, Check, X } from "lucide-react";

const INDY_CENTER = [39.78, -86.13];
const TILE_URL =
  "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png";

/* Single-hue indigo ramp keyed to saturation %. Used by the choropleth, the
 * inline progress bars in the inspector, and the legend strip at the bottom. */
function colorFor(pct) {
  if (pct < 5) return "#F3F2F6";
  if (pct < 15) return "#D9D8E4";
  if (pct < 25) return "#9AA0C0";
  if (pct < 40) return "#5B6196";
  return "#1B1F4B";
}

const SCOPE_LABEL = {
  designs:  "approved designs",
  projects: "active projects",
  permits:  "permits issued",
  parcels:  "vacant parcels eligible",
};

export default function CityHeatmap() {
  const { user } = useRole();
  const [style, setStyle] = useState(null);
  const [scope, setScope] = useState("designs");
  const [data, setData] = useState(null);
  const [selectedHood, setSelectedHood] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    setData(null);
    getHeatmap(user.id, { style, scope }).then((d) => alive && setData(d));
    return () => {
      alive = false;
    };
  }, [user?.id, style, scope]);

  // Selected polygon: the user-chosen hood if any, else null (Inspector shows
  // a default summary instead of a hood breakdown).
  const selectedPoly = useMemo(() => {
    if (!data || !selectedHood) return null;
    return data.polygons.find((p) => p.neighborhood === selectedHood) || null;
  }, [data, selectedHood]);

  const warnings = data?.warnings || [];
  const isDensityScope = scope === "permits" || scope === "parcels";

  // Scope-aware subtitle. The previous version hardcoded the style-saturation
  // sentence even when looking at permits/parcels, which was misleading.
  const subtitle = (() => {
    if (scope === "designs")
      return (
        <>
          Surface concentrations of any one architectural style by neighborhood.
          The City's saturation threshold is{" "}
          <strong className="tabular-nums" style={{ color: "#272636" }}>
            {data?.threshold_pct ?? 40}%
          </strong>
          .
        </>
      );
    if (scope === "projects")
      return (
        <>
          Active-project distribution by neighborhood. Darker indigo means
          higher concentration of one style among projects in flight.
        </>
      );
    if (scope === "permits")
      return (
        <>
          Relative density of <strong>permits issued (Accela)</strong> by
          neighborhood. Darker indigo means more permits.
        </>
      );
    return (
      <>
        Relative density of <strong>vacant parcels eligible</strong> for
        infill by neighborhood.
      </>
    );
  })();

  return (
    <section
      className="animate-fade-in-up"
      data-testid="city-heatmap-page"
    >
      {/* Header strip — eyebrow + title + subtitle + Export. Sits ABOVE the
          single bordered panel so it reads like the rest of the City surfaces. */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              style={{
                display: "inline-block",
                width: 28,
                height: 2,
                background: "#B14E2F",
              }}
            />
            <span className="wn-label" style={{ color: "#8F3C23" }}>
              City Staff · Quality Control
            </span>
          </div>
          <h1
            className="mt-3"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: "clamp(26px, 3.6vw, 32px)",
              fontWeight: 600,
              color: "#1B1F4B",
            }}
          >
            Neighborhood Heatmap.
          </h1>
          <p
            style={{
              color: "#6C6B85",
              fontSize: 14,
              marginTop: 6,
              maxWidth: 720,
              lineHeight: 1.55,
            }}
            data-testid="heatmap-subtitle"
          >
            {subtitle}
          </p>
        </div>
        <a
          href={exportUrl("heatmap", user?.id, {
            style: style || "",
            typology: "",
          })}
          className="wn-btn wn-btn-tertiary"
          data-testid="city-heatmap-export"
        >
          <Download size={14} /> Export CSV
        </a>
      </div>

      {/* Single bordered panel — all interactive surfaces live inside this
          container, hairline-divided. Nothing floats. */}
      <div
        className="mt-6"
        style={{
          background: "#FBFAFD",
          border: "1px solid #D9D8E4",
          borderRadius: 4,
          overflow: "hidden",
        }}
        data-testid="heatmap-panel"
      >
        {/* Filters strip ────────────────────────────────────────────────── */}
        <div
          style={{ padding: "16px 20px" }}
          data-testid="heatmap-filters"
        >
          <div className="flex items-center gap-3 flex-wrap">
            <span
              className="wn-label"
              style={{
                color: "#6C6B85",
                letterSpacing: "0.08em",
                fontSize: 10.5,
                minWidth: 44,
              }}
            >
              Layer
            </span>
            <div className="flex flex-wrap gap-1.5">
              <Chip
                active={scope === "designs"}
                onClick={() => setScope("designs")}
                testId="scope-chip-designs"
              >
                Approved designs
              </Chip>
              <Chip
                active={scope === "projects"}
                onClick={() => setScope("projects")}
                testId="scope-chip-projects"
              >
                Active projects
              </Chip>
              <Chip
                active={scope === "permits"}
                onClick={() => setScope("permits")}
                testId="scope-chip-permits"
              >
                Permits issued (Accela)
              </Chip>
              <Chip
                active={scope === "parcels"}
                onClick={() => setScope("parcels")}
                testId="scope-chip-parcels"
              >
                Vacant parcels eligible
              </Chip>
            </div>
          </div>
          {!isDensityScope && (
            <div className="mt-2.5 flex items-center gap-3 flex-wrap">
              <span
                className="wn-label"
                style={{
                  color: "#6C6B85",
                  letterSpacing: "0.08em",
                  fontSize: 10.5,
                  minWidth: 44,
                }}
              >
                Style
              </span>
              <div className="flex flex-wrap gap-1.5">
                <Chip
                  active={!style}
                  onClick={() => setStyle(null)}
                  testId="style-chip-all"
                >
                  All styles
                </Chip>
                {(data?.all_styles || []).map((s) => (
                  <Chip
                    key={s}
                    active={style === s}
                    onClick={() => setStyle(s)}
                    testId={`style-chip-${s}`}
                  >
                    {s.replace(/_/g, " ")}
                  </Chip>
                ))}
              </div>
            </div>
          )}
        </div>

        <Hairline />

        {/* Map + Inspector ─────────────────────────────────────────────── */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) 1px 340px",
            minHeight: 560,
          }}
          data-testid="heatmap-grid"
        >
          {/* Map */}
          <div
            style={{ position: "relative", background: "#fff" }}
            data-testid="heatmap-map"
          >
            <MapContainer
              center={INDY_CENTER}
              zoom={11}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom
              attributionControl={false}
            >
              {/* Empty-background click → clear the selected polygon.
                  Polygons stop propagation in their own click handler, so
                  this only fires when the user clicks outside any polygon. */}
              <MapBgClickHandler onClick={() => setSelectedHood(null)} />
              <TileLayer url={TILE_URL} className="wn-tiles" />
              {data?.polygons.map((p) => {
                const isSelected = p.neighborhood === selectedHood;
                return (
                  <Polygon
                    key={p.neighborhood}
                    positions={p.polygon.map((pt) => [pt.lat, pt.lng])}
                    pathOptions={{
                      fillColor: colorFor(p.saturation.pct),
                      fillOpacity: isSelected ? 0.78 : 0.55,
                      color: p.saturation.warning
                        ? "#B98A3C"
                        : isSelected ? "#1B1F4B" : "#5B6196",
                      weight: isSelected ? 3 : p.saturation.warning ? 3 : 1.5,
                      dashArray: p.saturation.warning ? "6 4" : undefined,
                    }}
                    eventHandlers={{
                      click: (e) => {
                        e.originalEvent.stopPropagation?.();
                        setSelectedHood(p.neighborhood);
                      },
                    }}
                  >
                    <Tooltip sticky direction="top" offset={[0, -2]}>
                      <div style={{ minWidth: 180 }}>
                        <div
                          style={{
                            fontFamily: '"Fraunces", Georgia, serif',
                            fontWeight: 600,
                            fontSize: 13,
                            color: "#1B1F4B",
                          }}
                        >
                          {p.neighborhood}
                        </div>
                        <div
                          className="tabular-nums"
                          style={{ fontSize: 11.5, color: "#272636" }}
                        >
                          {p.catalog_size} {SCOPE_LABEL[scope]} ·{" "}
                          {isDensityScope ? "density" : p.saturation.style}{" "}
                          {p.saturation.pct}%
                        </div>
                      </div>
                    </Tooltip>
                  </Polygon>
                );
              })}
            </MapContainer>
          </div>

          {/* Vertical hairline */}
          <div style={{ background: "#D9D8E4" }} aria-hidden />

          {/* Inspector */}
          <Inspector
            selectedPoly={selectedPoly}
            warnings={warnings}
            data={data}
            scope={scope}
            isDensityScope={isDensityScope}
            onClear={() => setSelectedHood(null)}
            onSelectFromWarning={(n) => setSelectedHood(n)}
          />
        </div>

        <Hairline />

        {/* Legend strip ────────────────────────────────────────────────── */}
        <Legend
          threshold={data?.threshold_pct ?? 40}
          isDensityScope={isDensityScope}
        />
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Inspector                                                               */
/* ────────────────────────────────────────────────────────────────────── */

function Inspector({
  selectedPoly,
  warnings,
  data,
  scope,
  isDensityScope,
  onClear,
  onSelectFromWarning,
}) {
  return (
    <div
      style={{
        padding: "18px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 18,
        minWidth: 0,
        background: "#FBFAFD",
      }}
      data-testid="heatmap-inspector"
    >
      {/* Aggregate warning summary — always visible, at the top */}
      <ConcentrationSummary
        warnings={warnings}
        onSelect={onSelectFromWarning}
        isDensityScope={isDensityScope}
      />

      <div style={{ height: 1, background: "#D9D8E4" }} aria-hidden />

      {/* Selected hood breakdown, or a default prompt */}
      {selectedPoly ? (
        <SelectedHood
          poly={selectedPoly}
          scope={scope}
          isDensityScope={isDensityScope}
          threshold={data?.threshold_pct ?? 40}
          onClear={onClear}
        />
      ) : (
        <DefaultInspectorPrompt scope={scope} data={data} />
      )}
    </div>
  );
}

function ConcentrationSummary({ warnings, onSelect, isDensityScope }) {
  // Density-based layers (permits, parcels) don't have a saturation model.
  if (isDensityScope) {
    return (
      <div data-testid="inspector-density-note">
        <div
          className="wn-label"
          style={{
            color: "#8F3C23",
            letterSpacing: "0.10em",
            fontSize: 10.5,
          }}
        >
          Layer note
        </div>
        <p
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 12.5,
            marginTop: 6,
            lineHeight: 1.5,
          }}
        >
          The saturation model does not apply to this layer. Polygon shading
          encodes relative density instead.
        </p>
      </div>
    );
  }

  if (warnings.length === 0) {
    return (
      <div data-testid="inspector-warnings-empty">
        <div
          className="wn-label"
          style={{
            color: "#8F3C23",
            letterSpacing: "0.10em",
            fontSize: 10.5,
          }}
        >
          Concentration
        </div>
        <div
          className="mt-2 flex items-center"
          style={{ gap: 8, fontSize: 12.5, color: "#4F6149" }}
        >
          <Check size={14} strokeWidth={2.2} />
          All neighborhoods balanced at the current filter.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="inspector-warnings">
      <div
        className="wn-label"
        style={{
          color: "#8F3C23",
          letterSpacing: "0.10em",
          fontSize: 10.5,
        }}
      >
        Concentration · {warnings.length} over threshold
      </div>
      <ul className="mt-2 space-y-1.5">
        {warnings.map((w) => (
          <li
            key={`${w.neighborhood}-${w.style}`}
            className="flex items-baseline justify-between gap-3"
            data-testid={`heatmap-warning-${w.neighborhood}`}
          >
            <button
              type="button"
              onClick={() => onSelect(w.neighborhood)}
              style={{
                color: "#1B1F4B",
                fontWeight: 600,
                fontSize: 12.5,
                textAlign: "left",
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
              className="hover:underline"
            >
              <AlertTriangle
                size={12}
                style={{
                  color: "#B98A3C",
                  display: "inline",
                  marginRight: 4,
                  marginBottom: 2,
                }}
              />
              {w.neighborhood}
              <span
                style={{
                  fontFamily: '"Newsreader", Georgia, serif',
                  fontStyle: "italic",
                  fontSize: 11.5,
                  color: "#6C6B85",
                  fontWeight: 400,
                  marginLeft: 6,
                }}
              >
                {w.style.replace(/_/g, " ")}
              </span>
            </button>
            <span
              className="tabular-nums"
              style={{ color: "#8a651f", fontSize: 12.5, fontWeight: 700 }}
            >
              {w.pct}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DefaultInspectorPrompt({ scope, data }) {
  const total = data?.polygons?.length || 0;
  const totalRecords = (data?.polygons || []).reduce(
    (s, p) => s + (p.catalog_size || 0),
    0,
  );
  return (
    <div data-testid="inspector-default">
      <div
        className="wn-label"
        style={{ color: "#8F3C23", letterSpacing: "0.10em", fontSize: 10.5 }}
      >
        Inspector
      </div>
      <p
        style={{
          color: "#272636",
          fontSize: 13,
          marginTop: 8,
          lineHeight: 1.5,
        }}
      >
        Click any neighborhood on the map to see its breakdown.
      </p>
      <div className="mt-4">
        <div
          className="wn-label"
          style={{ color: "#6C6B85", letterSpacing: "0.06em", fontSize: 10 }}
        >
          Across all neighborhoods
        </div>
        <div
          className="tabular-nums mt-1"
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontSize: 22,
            fontWeight: 600,
            color: "#1B1F4B",
            lineHeight: 1.1,
          }}
        >
          {totalRecords}
        </div>
        <div
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            fontSize: 12,
            color: "#6C6B85",
          }}
        >
          {SCOPE_LABEL[scope]} across {total} neighborhood{total === 1 ? "" : "s"}
        </div>
      </div>
    </div>
  );
}

function SelectedHood({ poly, scope, isDensityScope, threshold, onClear }) {
  // Build the distribution. For density-based layers (permits, parcels) the
  // backend doesn't return a style breakdown, so we show a tiny summary card
  // instead of the bar chart.
  const distEntries = Object.entries(poly.style_distribution_pct || {})
    .sort((a, b) => b[1] - a[1])
    .filter(([, pct]) => pct > 0);

  return (
    <div data-testid="inspector-selected">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div
            className="wn-label"
            style={{
              color: "#8F3C23",
              letterSpacing: "0.10em",
              fontSize: 10.5,
            }}
          >
            Selected
          </div>
          <h3
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 17,
              color: "#1B1F4B",
              marginTop: 2,
              lineHeight: 1.2,
            }}
            data-testid="inspector-selected-name"
          >
            {poly.neighborhood}
          </h3>
        </div>
        <button
          type="button"
          onClick={onClear}
          className="wn-btn wn-btn-tertiary"
          style={{ padding: 4 }}
          aria-label="Clear selection"
          data-testid="inspector-clear"
        >
          <X size={14} />
        </button>
      </div>

      <div
        className="mt-2 tabular-nums"
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          fontSize: 12.5,
          color: "#6C6B85",
        }}
      >
        {poly.catalog_size} {SCOPE_LABEL[scope]}
        {!isDensityScope && (
          <> · saturation {poly.saturation.pct}%</>
        )}
      </div>

      {/* Warning callout if this hood is over the threshold */}
      {!isDensityScope && poly.saturation.warning && (
        <div
          className="mt-3 flex items-start gap-2"
          style={{
            background: "rgba(185,138,60,0.12)",
            border: "1px solid #B98A3C",
            borderRadius: 4,
            padding: "8px 10px",
          }}
          data-testid="inspector-selected-warning"
        >
          <AlertTriangle
            size={12}
            style={{ color: "#8a651f", marginTop: 3, flexShrink: 0 }}
          />
          <p style={{ fontSize: 12, color: "#272636", margin: 0, lineHeight: 1.5 }}>
            <strong>{poly.saturation.pct}%</strong>{" "}
            {poly.saturation.style.replace(/_/g, " ")} — over the City's{" "}
            {threshold}% threshold.
          </p>
        </div>
      )}

      {/* Style distribution bars */}
      {distEntries.length > 0 && (
        <div className="mt-4">
          <div
            className="wn-label"
            style={{
              color: "#6C6B85",
              letterSpacing: "0.06em",
              fontSize: 10,
              marginBottom: 6,
            }}
          >
            Style mix
          </div>
          <div className="space-y-2">
            {distEntries.map(([s, pct]) => (
              <div
                key={s}
                data-testid={`inspector-style-${s}`}
              >
                <div
                  className="flex items-baseline justify-between"
                  style={{ fontSize: 12 }}
                >
                  <span style={{ color: "#272636" }}>
                    {s.replace(/_/g, " ")}
                  </span>
                  <span
                    className="tabular-nums"
                    style={{ color: "#1B1F4B", fontWeight: 700 }}
                  >
                    {pct}%
                  </span>
                </div>
                <div
                  style={{
                    height: 4,
                    background: "#F3F2F6",
                    borderRadius: 2,
                    marginTop: 3,
                  }}
                >
                  <div
                    style={{
                      height: 4,
                      width: `${pct}%`,
                      background: pct >= threshold ? "#B98A3C" : colorFor(pct),
                      borderRadius: 2,
                      transition: "width 200ms ease",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isDensityScope && (
        <p
          className="mt-3"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 12.5,
            lineHeight: 1.5,
          }}
        >
          Density layers don't carry a style mix. Switch to <em>Approved
          designs</em> to inspect this hood's architectural distribution.
        </p>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Legend                                                                  */
/* ────────────────────────────────────────────────────────────────────── */

function Legend({ threshold, isDensityScope }) {
  // Discrete stops keyed to colorFor(). Marker at the threshold is only
  // shown for style-saturation layers.
  const stops = [
    { pct: 0,  color: "#F3F2F6" },
    { pct: 5,  color: "#D9D8E4" },
    { pct: 15, color: "#9AA0C0" },
    { pct: 25, color: "#5B6196" },
    { pct: 40, color: "#1B1F4B" },
  ];
  return (
    <div
      style={{
        padding: "14px 20px",
        display: "flex",
        alignItems: "center",
        gap: 16,
        flexWrap: "wrap",
        background: "#FBFAFD",
      }}
      data-testid="heatmap-legend"
    >
      <span
        className="wn-label"
        style={{ color: "#6C6B85", letterSpacing: "0.08em", fontSize: 10.5 }}
      >
        {isDensityScope ? "Density" : "Saturation"}
      </span>
      <div style={{ position: "relative", flex: "1 1 280px", maxWidth: 540 }}>
        <div
          style={{
            display: "flex",
            height: 8,
            borderRadius: 4,
            overflow: "hidden",
            border: "1px solid #D9D8E4",
          }}
          aria-hidden
        >
          {stops.map((s, i) => (
            <div
              key={s.pct}
              style={{
                flex: 1,
                background: s.color,
                borderLeft: i === 0 ? "none" : "1px solid #FBFAFD",
              }}
            />
          ))}
        </div>
        {!isDensityScope && (
          <div
            style={{
              position: "absolute",
              left: `${(threshold / 100) * 100}%`,
              top: -6,
              transform: "translateX(-50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              fontSize: 10,
              color: "#8a651f",
              fontWeight: 700,
              letterSpacing: "0.05em",
              whiteSpace: "nowrap",
            }}
            data-testid="legend-threshold-marker"
          >
            <span style={{ marginBottom: -2 }}>▼</span>
            <span style={{ marginTop: 12 }}>THRESHOLD {threshold}%</span>
          </div>
        )}
        <div
          className="tabular-nums"
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 6,
            fontSize: 10.5,
            color: "#6C6B85",
          }}
        >
          <span>0%</span>
          <span>100%</span>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Helpers                                                                 */
/* ────────────────────────────────────────────────────────────────────── */

function MapBgClickHandler({ onClick }) {
  // react-leaflet v4 doesn't honour an `eventHandlers` prop on MapContainer;
  // map-level events must be subscribed via this hook from inside the map.
  // Polygon click events propagate up to the map click — so we skip the
  // deselect when the click target was a polygon path.
  useMapEvents({
    click: (e) => {
      const target = e.originalEvent?.target;
      if (target && target.tagName === "path") return;
      onClick();
    },
  });
  return null;
}

function Hairline() {
  return <div style={{ height: 1, background: "#D9D8E4" }} aria-hidden />;
}

function Chip({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      style={{
        padding: "4px 10px",
        borderRadius: 999,
        border: "1px solid " + (active ? "#B14E2F" : "#D9D8E4"),
        background: active ? "#B14E2F" : "#FBFAFD",
        color: active ? "#FBFAFD" : "#272636",
        fontSize: 11.5,
        fontWeight: 600,
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}
